import React, { useCallback, useMemo } from 'react';
import Typography from '@material-ui/core/Typography';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Attachment } from '@material-ui/icons';
import { EmailSearchResult } from '../../api-client/autogenerated';
import { Tooltip } from '@material-ui/core';

const useStyles = makeStyles(() =>
  createStyles({
    documentItem: {
      padding: 16,
      paddingLeft: 47,
      border: '1px solid transparent',
      cursor: 'pointer',
      position: 'relative',
      '&:hover': {
        backgroundColor: '#F5F5F5',
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        left: '24px',
        top: -2,
        bottom: 0,
        width: '2px',
        backgroundColor: '#0947B9',
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        left: '24px',
        top: 24,
        width: '12px',
        height: '2px',
        backgroundColor: '#0947B9',
      },
      '&:last-child': {
        '&::before': {
          height: 28,
        },
      },
    },
    fileItem: {
      '&:hover': {
        backgroundColor: '#E0E0E0',
      },
    },
  }),
);

interface Props {
  email: EmailSearchResult;
  searchTerm: string;
  keywordSearchTerms: string[];
  filters: {
    createdBeforeDate?: string;
    createdAfterDate?: string;
  };
}

export default function EmailSearchItem(props: Props) {
  const { email, searchTerm, keywordSearchTerms, filters } = props;
  const classes = useStyles();

  const fileSources = email.sources.filter((x) => x !== 'body');

  const handleSelectEmail = useCallback(() => {
    const params = new URLSearchParams();
    params.set('id', email.id);
    params.set(
      'query',
      keywordSearchTerms.filter(Boolean).length > 0
        ? searchTerm + ` ${keywordSearchTerms.join(' ')}`
        : searchTerm,
    );
    if (filters.createdAfterDate) params.set('after', filters.createdAfterDate);
    if (filters.createdBeforeDate) params.set('before', filters.createdBeforeDate);

    window.open(
      `/main/projects/${email.projectId}/correspondence-log?${params.toString()}`,
      '_blank',
    );
  }, [email]);

  const content = useMemo(
    () => (
      <div key={email.id} onClick={handleSelectEmail} className={classes.documentItem}>
        <Typography style={{ fontSize: '16px', fontWeight: 600, marginBottom: 8 }}>
          {email.subject}
        </Typography>

        <Typography>
          From: {email.senderName} {'<'}
          {email.senderAddress}
          {'>'}
        </Typography>

        {fileSources.length > 0 ? <div style={{ marginTop: 12 }} /> : null}

        {fileSources.map((s) => {
          if (s === 'body') return null;

          return (
            <div key={s} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <Attachment htmlColor="#7A797A" style={{ marginRight: 4 }} />
              <Typography
                style={{
                  width: '100%',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {s}
              </Typography>
            </div>
          );
        })}
      </div>
    ),
    [email, classes, handleSelectEmail, fileSources],
  );

  return (
    <Tooltip arrow title="View Email" placement="right-start">
      {content}
    </Tooltip>
  );
}
